import { useEffect, useRef } from 'react';

/**
 * Avisos push en el APK.
 *
 * Solo hace algo dentro de Capacitor. En el navegador no existe la parte
 * nativa, y la web de la caseta —que es la misma compilación— no debe pedir
 * permiso de notificaciones a un guardia que está viendo la pantalla.
 *
 * El plugin se carga con `import()` y no arriba del archivo a propósito: así
 * Vite lo deja en un trozo aparte que el navegador nunca descarga, igual que
 * `servidor.ts` evita importar `@capacitor/core`.
 */

import { pedir } from './api';
import { esAppNativa } from './servidor';

/**
 * Canal de Android para los avisos de visita.
 *
 * Tiene que existir antes de que llegue el primer mensaje: desde Android 8 un
 * push que apunta a un canal inexistente **no se muestra** y no hay error en
 * ningún lado. El nombre lo ve el usuario en los ajustes del teléfono, donde
 * puede silenciarlo sin desactivar toda la app.
 *
 * El identificador está duplicado en `apps/relay/src/fcm.ts`, que es quien lo
 * pone en el mensaje. Si cambia aquí, cambia allá.
 */
const CANAL = 'kodica_visitas';

/** Evita registrar dos veces si la sesión se revalida. */
let yaRegistrado = false;

/** Lo que la app recibe cuando llega un aviso con la pantalla abierta. */
export interface AvisoRecibido {
  titulo: string;
  cuerpo: string;
}

/** Nombre del evento que dispara este módulo para la interfaz. */
export const EVENTO_AVISO = 'kodica:aviso';

function anunciarEnLaApp(aviso: AvisoRecibido): void {
  window.dispatchEvent(new CustomEvent<AvisoRecibido>(EVENTO_AVISO, { detail: aviso }));
}

function plataforma(): 'android' | 'ios' {
  const cap = (globalThis as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  return cap?.getPlatform?.() === 'ios' ? 'ios' : 'android';
}

/**
 * Pide permiso, obtiene el token de Firebase y lo deja en la caseta.
 *
 * Se llama en cada arranque con sesión abierta, no una sola vez: Firebase
 * cambia el token al reinstalar la app o al restaurar un respaldo, y si no se
 * reenvía, el residente deja de recibir avisos sin que nada falle a la vista.
 *
 * Nunca lanza. Un teléfono sin permiso de notificaciones tiene que seguir
 * abriendo la reja: los avisos son un extra, no el producto.
 */
export async function registrarParaAvisos(): Promise<void> {
  if (!esAppNativa() || yaRegistrado) return;
  yaRegistrado = true;

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    let permiso = await PushNotifications.checkPermissions();
    if (permiso.receive === 'prompt' || permiso.receive === 'prompt-with-rationale') {
      permiso = await PushNotifications.requestPermissions();
    }
    if (permiso.receive !== 'granted') {
      // El usuario dijo que no. Se respeta y no se vuelve a preguntar en esta
      // sesión; Android tampoco deja insistir.
      console.info('[avisos] sin permiso de notificaciones');
      yaRegistrado = false;
      return;
    }

    if (plataforma() === 'android') {
      await PushNotifications.createChannel({
        id: CANAL,
        name: 'Visitas',
        description: 'Cuando alguien entra a tu vivienda',
        importance: 5, // Máxima: aparece en pantalla, no solo en la barra.
        visibility: 1, // Visible en la pantalla de bloqueo, que es donde se lee.
      });
    }

    // Los escuchas van antes de `register()`: el token puede llegar de
    // inmediato y sin escucha puesto se pierde sin dejar rastro.
    await PushNotifications.addListener('registration', (t) => {
      void pedir('/aparatos', {
        metodo: 'POST',
        cuerpo: { token: t.value, plataforma: plataforma() },
      }).catch((e: unknown) => {
        // La caseta puede estar apagada. No se reintenta aquí: el siguiente
        // arranque con sesión abierta vuelve a mandarlo.
        console.warn('[avisos] no se pudo registrar el aparato', e);
      });
    });

    await PushNotifications.addListener('registrationError', (e) => {
      // Casi siempre es un `google-services.json` que no corresponde al
      // paquete, y el mensaje de Firebase lo dice.
      console.error('[avisos] Firebase rechazó el registro', e);
    });

    // Con la app abierta, Android **no** dibuja la notificación: se la entrega
    // a la aplicación y da por hecho que ya está mostrando lo que haga falta.
    // Sin esto, un residente con la app en pantalla no vería nada llegar.
    await PushNotifications.addListener('pushNotificationReceived', (n) => {
      anunciarEnLaApp({ titulo: n.title ?? 'Kodica', cuerpo: n.body ?? '' });
    });

    await PushNotifications.register();
  } catch (e) {
    console.error('[avisos] no se pudieron activar', e);
    yaRegistrado = false;
  }
}

/** Al cerrar sesión: el siguiente que entre tiene que registrar lo suyo. */
export function olvidarRegistroDeAvisos(): void {
  yaRegistrado = false;
}

/**
 * Vuelve a pedir los datos cuando llega un aviso o cuando se regresa a la app.
 *
 * ## Por qué hacen falta las dos cosas
 *
 * **Con la pantalla abierta**, Android no dibuja la notificación: se la entrega
 * a la aplicación. Ahí llega por `EVENTO_AVISO` y basta con escucharlo.
 *
 * **Con la pantalla apagada o la app en segundo plano**, el sistema la dibuja
 * él y la aplicación no se entera de nada. Cuando el residente vuelve —tocando
 * la notificación o abriendo la app— la pantalla es la que dejó, con el
 * contador de antes. Por eso también se escucha `visibilitychange`: volver es
 * el único momento en que se puede saber que algo pasó mientras no mirábamos.
 *
 * Sin esto, el residente veía el aviso llegar al teléfono y la tarjeta seguía
 * diciendo lo mismo — que es peor que no tener contador, porque parece que el
 * aviso se perdió.
 */
export function useAlLlegarAviso(recargar: () => void): void {
  // En una referencia y no en las dependencias del efecto: si no, cada render
  // del que llama volvería a suscribir y desuscribir los dos escuchadores.
  const ultima = useRef(recargar);
  ultima.current = recargar;

  useEffect(() => {
    const alLlegar = () => ultima.current();
    const alVolver = () => {
      if (document.visibilityState === 'visible') ultima.current();
    };

    window.addEventListener(EVENTO_AVISO, alLlegar);
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      window.removeEventListener(EVENTO_AVISO, alLlegar);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, []);
}
